import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import type { SourceCohortDisplay, UseSourceCohortSectionResult } from '../types';
import { useAnalysisEditLock } from './useAnalysisEditLock';
import { useAnalysisRouteParams } from './useAnalysisRouteParams';
import { useAnalysisSourceCohort } from './useAnalysisSourceCohort';

// `parentLoading` is the section's own `isLoading` prop (the definition tab
// still hydrating) — combined here with the source-cohort fetch's loading
// state so the container only needs a single `isLoading` flag to render.
export const useSourceCohortSection = (parentLoading = false): UseSourceCohortSectionResult => {
  const { formatMessage } = useIntl();
  const { projectId } = useAnalysisRouteParams();
  const { selectedCohort, isLoading: isSectionLoading } = useAnalysisSourceCohort();
  const { isEditDisabled, isProcessing } = useAnalysisEditLock();
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const cohort = useMemo<SourceCohortDisplay | null>(() => {
    if (!selectedCohort) return null;
    return {
      cohortProfileId: selectedCohort.cohortProfileId,
      cohortName: selectedCohort.cohortName,
      status: selectedCohort.latestStatus,
      subjectCount: selectedCohort.latestSubjectCount,
    };
  }, [selectedCohort]);

  // Not-yet-PROCESSED cohorts have no real subject count (null) — show a
  // placeholder rather than hiding the caption.
  const metaLine = useMemo(() => {
    if (!cohort) return '';
    return cohort.subjectCount !== null && cohort.subjectCount >= 0
      ? `${cohort.subjectCount.toLocaleString()} subjects`
      : 'No subjects';
  }, [cohort]);

  const onAddCohort = useCallback(() => setIsPickerOpen(true), []);
  const onClosePicker = useCallback(() => setIsPickerOpen(false), []);

  const editLockTooltip = isEditDisabled
    ? formatMessage({
        id: isProcessing
          ? 'analysisDetail.editLock.processing'
          : 'analysisDetail.editLock.notOwner',
      })
    : undefined;

  return {
    projectId,
    sectionLabel: formatMessage({ id: 'analysisDetail.sourceCohort.label' }),
    // Always "Add cohort" — no "Replace cohort" variant, even with one attached.
    addLabel: formatMessage({ id: 'analysisDetail.sourceCohort.addButton' }),
    notSelectedText: formatMessage({ id: 'analysisDetail.sourceCohort.notSelected' }),
    cohort,
    metaLine,
    isLoading: parentLoading || isSectionLoading,
    isPickerOpen,
    onAddCohort,
    onClosePicker,
    isEditDisabled,
    editLockTooltip,
  };
};
